import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import {
  fontSize,
  minBreakpointQuery,
  standardColours,
  zIndexLayers,
} from '../styles';
import { DateRange, Link } from './ui';

const StyledEventCard = styled.article`
  position: relative;
  color: ${standardColours.white};
`;

const StyledLink = styled(Link)`
  height: 100%;
`;

const StyledImage = styled(GatsbyImage)`
  height: 100%;
  border-radius: 15px;
`;

const StyledContent = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  justify-content: end;
  flex-direction: column;
  background: linear-gradient(
    0deg,
    ${standardColours.black} 0%,
    ${standardColours.transparent} 40%
  );
  padding-bottom: 25px;
  padding-left: 30px;
  padding-right: 30px;
  border-radius: 15px;
`;

const StyledDate = styled(DateRange)`
  position: absolute;
  top: 20px;
  left: 20px;
  max-width: calc(100% - 40px);
  z-index: ${zIndexLayers.first};
`;

const StyledTitle = styled.h3`
  ${fontSize(18)};

  ${minBreakpointQuery.large`
    ${fontSize(20)};
  `}
`;

const StyledLocation = styled.p`
  margin-top: 5px;
`;

const EventCard = event => {
  const {
    title,
    featuredImage,
    startDate,
    startDateFormatted,
    endDate,
    endDateFormatted,
    venue,
    isVenuePage,
    ...props
  } = event;

  const {
    datoCmsEventsArchive: { fallbackImage },
  } = useStaticQuery(graphql`
    query EventCardQuery {
      datoCmsEventsArchive {
        fallbackImage {
          gatsbyImageData(
            width: 560
            height: 640
            imgixParams: { w: "560", h: "640" }
          )
          alt
        }
      }
    }
  `);

  const image = featuredImage || fallbackImage;

  return (
    <StyledEventCard {...props}>
      <StyledLink to={event}>
        <StyledImage image={image.gatsbyImageData} alt={image.alt} />
        <StyledContent>
          <StyledDate
            startDate={startDate}
            startDateFormatted={startDateFormatted}
            endDate={endDate}
            endDateFormatted={endDateFormatted}
          />
          <StyledTitle {...(isVenuePage && { as: 'h4' })}>{title}</StyledTitle>
          <StyledLocation>
            {venue.name}
            {venue.region && `, ${venue.region}`}
          </StyledLocation>
        </StyledContent>
      </StyledLink>
    </StyledEventCard>
  );
};

export default EventCard;

export const EventCardFragment = graphql`
  fragment EventCardFragment on DatoCmsEvent {
    title
    featuredImage {
      gatsbyImageData(
        width: 560
        height: 640
        imgixParams: { w: "560", h: "640" }
      )
      alt
    }
    startDate
    startDateFormatted: startDate(formatString: "Do MMM YYYY")
    endDate
    endDateFormatted: endDate(formatString: "Do MMM YYYY")
    monthYear: startDate(formatString: "MMMM YYYY")
    venue {
      name
      region
    }
    ...LinkFragment
  }
`;
